import { Component, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { NgClass, NgIf } from "@angular/common";
import { AllReferralsComponent } from "../admin/all-referrals/all-referrals.component";
import { environment } from "../../../environments/environment";
import { Environment } from "../../../environments/environment.model";
import { StateManagementService } from "../../services/state-management.service";
import { UtilityService } from "../../services/utility.service";
import { ActivatedRoute } from "@angular/router";
import { Subject, takeUntil, switchMap, Observable, of, catchError } from "rxjs";
import { UsStates } from "../../utility/us-states";
import { StateAssociation } from "../../models/state.association.model";
import { StateAssociationService } from "../../services/state.association.service";
import { FeatureFlagService } from "../../services/feature-flag.service";
import { FeatureFlagEnum } from "../../models/feature-flag.enum";

@Component({
  selector: "app-public",
  standalone: true,
  imports: [AllReferralsComponent, NgIf, NgClass],
  templateUrl: "./public.component.html",
  styleUrl: "./public.component.scss"
})
export class PublicComponent implements OnInit, OnDestroy {
  protected readonly environment: Environment = environment;
  protected pageTitle: string | null = null;
  protected columnsToDisplay: string[] = [
    "addDate",
    "type",
    "category",
    "city",
    "businessLines",
    "industry",
    "noOfEmployees",
    "yearsInBusiness",
    "status",
    "isMobileView"
  ];
  private destroy$ = new Subject<void>();
  protected stateAssociation?: StateAssociation;
  protected showStateLogo: boolean = false;
  protected logoImageUrl?: string;
  protected isLoadingLogo: boolean = true;

  private stateLogosCache = new Map<string, string>();
  private loadingStates = new Set<string>();

  constructor(
    private renderer: Renderer2,
    private stateManagementService: StateManagementService,
    private route: ActivatedRoute,
    private stateAssociationService: StateAssociationService,
    private featureFlagService: FeatureFlagService
  ) {
    if (UtilityService.getNonSubDomainUrlHostName().includes("trustedchoice")) {
      this.pageTitle = "Insurance Referral Feed | Trusted Choice";
    } else {
      this.pageTitle = "Insurance Referral Feed | Momentum Edge";
    }
  }

  ngOnInit(): void {
    this.addInlineScript();
    this.loadScripts();

    this.route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        switchMap((params) => {
          const stateParam = params["state"];
          const stateName = UsStates.getStateName(stateParam);

          this.showStateLogo = false;
          this.logoImageUrl = undefined;
          this.stateAssociation = undefined;

          if (stateName) {
            this.isLoadingLogo = true;
            return this.stateAssociationService.getStateAssociation(
              UsStates.getStateId(stateParam)
            );
          }

          this.isLoadingLogo = false;
          return of(null);
        })
      )
      .subscribe({
        next: (stateAssociation) => {
          if (stateAssociation) {
            if (
              stateAssociation.memberLevel != null &&
              stateAssociation.memberLevel > 0 &&
              this.featureFlagService.isEnabled(FeatureFlagEnum.STATE_PARTNER_ENABLED_FEATURE_FLAG)
            ) {
              this.stateAssociation = stateAssociation;
              this.loadStateAssociationLogo(stateAssociation.state!);
            } else {
              this.isLoadingLogo = false;
              this.stateAssociation = undefined;
              this.logoImageUrl = undefined;
            }
          } else {
            this.isLoadingLogo = false;
            this.stateAssociation = undefined;
            this.logoImageUrl = undefined;
          }
        }
      });
  }

  private loadStateAssociationLogo(stateName: string): void {
    const stateCode = UsStates.states.find((s) => s.displayName === stateName)?.field;

    if (!stateCode) {
      this.isLoadingLogo = false;
      return;
    }

    if (this.stateLogosCache.has(stateCode)) {
      this.logoImageUrl = this.stateLogosCache.get(stateCode);
      this.isLoadingLogo = false;
      return;
    }

    const stateId = UsStates.getStateId(stateCode);

    if (!stateId) {
      console.warn(`No ID found for state: ${stateCode}`);
      this.isLoadingLogo = false;
      return;
    }

    this.stateAssociationService
      .getStateAssociationImage(stateId)
      .pipe(
        switchMap((blob) => {
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error(`Failed to read blob for state ${stateCode}`));
            reader.readAsDataURL(blob);
          });
        }),
        catchError((err) => {
          console.warn(`Failed to load logo for state ${stateCode}:`, err);
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (logoDataUrl) => {
          if (logoDataUrl) {
            this.stateLogosCache.set(stateCode, logoDataUrl);
            this.logoImageUrl = logoDataUrl;
          } else {
            this.logoImageUrl = undefined;
          }
          this.isLoadingLogo = false;
        },
        error: (err) => {
          console.error("Error loading state logo:", err);
          this.logoImageUrl = undefined;
          this.isLoadingLogo = false;
        }
      });
  }

  getSkinClass(): string {
    if (this.isMomentumEdge()) {
      return "momentumedge-theme";
    }

    if (this.isAgentium()) {
      return "agentium-theme";
    }

    return "";
  }

  isMomentumEdge(): boolean {
    const domain = this.getDomainWithoutTld();
    return domain === "trustedchoice" || domain === "momentumedge";
  }

  isAgentium(): boolean {
    return !this.isMomentumEdge();
  }

  getDomainWithoutTld(): string | null {
    let fullHostnameArray: string[] = window.location.hostname.split(".");

    if (fullHostnameArray == null || fullHostnameArray.length === 0) {
      return null;
    }

    if (fullHostnameArray.length === 1) {
      return fullHostnameArray[0];
    }

    return fullHostnameArray[fullHostnameArray.length - 2];
  }

  isMegaMenuOpen = false;

  toggleMegaMenu() {
    this.isMegaMenuOpen = !this.isMegaMenuOpen;
  }

  loadScripts(): void {
    const scripts = [
      { src: "//cs.iubenda.com/sync/2741231.js" },
      { src: "//cdn.iubenda.com/cs/gpp/stub.js" },
      { src: "//cdn.iubenda.com/cs/iubenda_cs.js", charset: "UTF-8", async: true },
      {
        src: "https://cdn.popupsmart.com/bundle.js",
        attrs: { "data-id": "11875" },
        async: true,
        defer: true
      }
    ];

    scripts.forEach((scriptInfo): void => {
      const script = this.renderer.createElement("script");
      script.src = scriptInfo.src;
      script.type = "text/javascript";
      script.async = true;

      if (scriptInfo.charset) {
        script.charset = scriptInfo.charset;
      }

      if (scriptInfo.attrs) {
        for (const [key, value] of Object.entries(scriptInfo.attrs)) {
          script.setAttribute(key, value);
        }
      }

      if (scriptInfo.defer) {
        script.defer = true;
      }

      this.renderer.appendChild(document.head, script);
    });
  }

  addInlineScript(): void {
    const scriptContent = `
      var _iub = _iub || [];
        _iub.csConfiguration = {
          countryDetection: true,
          enableGdpr: false,
          enableUspr: true,
          lang: "en",
          siteId: 2741231,
          tcfPurposes: {
            2: "consent_only",
            7: "consent_only",
            8: "consent_only",
            9: "consent_only",
            10: "consent_only",
          },
          usprPurposes: "s,sh,adv",
          cookiePolicyId: 75803826,
          banner: {
            backgroundColor: "#FFFFFF",
            closeButtonDisplay: false,
            position: "float-bottom-left",
            textColor: "#000000",
          },
        };
    `;

    const script: any = this.renderer.createElement("script");
    script.type = "text/javascript";
    script.text = scriptContent;
    this.renderer.appendChild(document.head, script);
  }

  currentYear(): number {
    return new Date().getFullYear();
  }

  ngOnDestroy(): void {
    this.stateManagementService.resetHeadingAndTitle();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
